class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then(async (state) => {
        await CartDrawerActionsInstance.finishEventListeners();
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        await CartDrawerActionsInstance.initializeCart();
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

class CartDrawerActions {
  constructor() {
    this.count = 0;
  }

  init() {
      document.addEventListener('DOMContentLoaded', async () => {
          const cart = await this.GetCart();
          console.log(cart);
          if(cart.item_count > 0){
              await this.initializeCart();
              this.count = cart.item_count;
          }
      });
  }

  async initializeCart() {
      const cart = await this.GetCart();
      console.log(cart);
      await this.ApplyPostalCodeMask('input[name="shipping"]');
      await this.UpdateResume(cart);
      await this.initializeEventListeners();
  }

  async finishEventListeners() {
    const buttonShipping = document.getElementById('button-shipping');
    buttonShipping.removeEventListener('click', this.handlerShipping);

    const buttonDiscount = document.getElementById('button-discount');
    buttonDiscount.removeEventListener('click', this.handlerDiscount);

    const selectShipping = document.getElementById('shipping-select');
    selectShipping.removeEventListener('change', this.handlerSelect);
  }

  async initializeEventListeners() {
      const buttonShipping = document.getElementById('button-shipping');
      buttonShipping.addEventListener('click', this.handlerShipping);

      const buttonDiscount = document.getElementById('button-discount');
      buttonDiscount.addEventListener('click', this.handlerDiscount);

      const selectShipping = document.getElementById('shipping-select');
      selectShipping.addEventListener('change', this.handlerSelect);

      const local_storage_postalcode = localStorage.getItem('postalcode');
      if (local_storage_postalcode != undefined && local_storage_postalcode != "") {
          document.getElementById('shipping').value = local_storage_postalcode;
          buttonShipping.dispatchEvent(new Event('click'));
      }
  }

  async handlerShipping(el){
    const postalCode = document.getElementById('shipping').value;
    const shippingOptionsElement = document.querySelector('.row-shipping-options');
    if(postalCode != "" && postalCode != undefined) {
      const shippingRates = await CartDrawerActionsInstance.CalculateShipping(postalCode);
      if(shippingRates.status == 200) {
        const selectElement = document.getElementById('shipping-select');
        selectElement.innerHTML = '';
        shippingRates.data.forEach(item => {
          const option = document.createElement('option');
          option.text = item.price > 0 ? `${item.code}: R$ ${parseFloat(item.price).toFixed(2).replace('.', ',')}` : `${item.code}: Grátis`;
          option.value = item.price;
          selectElement.appendChild(option);
        });
        shippingOptionsElement.classList.remove('d-none');
        var cart = await CartDrawerActionsInstance.GetCart();
        CartDrawerActionsInstance.UpdateResume(cart);
        localStorage.setItem("shipping", shippingRates);
        localStorage.setItem("postalcode", postalCode);
      } else {
        shippingOptionsElement.classList.add('d-none');
      }
    } else {
      shippingOptionsElement.classList.add('d-none');
      const errorLabel = document.querySelector('.error-shipping');
      errorLabel.style.display = 'inline-block';
      setTimeout(function(){
        errorLabel.style.display = 'none';
      }, 5000);
    }
  }

  async handlerSelect(el){
    const cart = await CartDrawerActionsInstance.GetCart();
    CartDrawerActionsInstance.UpdateResume(cart);
  }

  async handlerDiscount(el){
    const discountCode = document.getElementById('coupon').value;
    const errorLabel = document.querySelector('.error-coupon');
    if(discountCode != "" && discountCode != undefined) {
      var cart = await CartDrawerActionsInstance.GetCart();
      if(cart.token) {
        const coupon = document.getElementById('coupon').value;
        const couponApply = await CartDrawerActionsInstance.AddCartCoupon(cart.token, coupon);
        cart = await CartDrawerActionsInstance.GetCart();
        if(cart.total_discount != 0) {
          CartDrawerActionsInstance.UpdateResume(cart);
        } else {
          errorLabel.style.display = 'inline-block';
          await CartDrawerActionsInstance.AddCartCoupon(cart.token, '');
          CartDrawerActionsInstance.UpdateResume(cart);
          setTimeout(function(){
            errorLabel.style.display = 'none';
          }, 5000);
        }
      }           
    } else {
      errorLabel.style.display = 'inline-block';
      setTimeout(function(){
        errorLabel.style.display = 'none';
      }, 5000);
    }
  }

  async CalculateShipping(postalCode) {
    var response = "";
    try{
        response = await fetch(`/cart/shipping_rates.json?shipping_address%5Bzip%5D=${postalCode}&shipping_address%5Bcountry%5D=Brazil&shipping_address%5Bprovince%5D=S%C3%A3o%20Paulo`, {
            method: 'GET',
        })
        .then(response => response.json())
        .then(data => {
            if (data.shipping_rates && data.shipping_rates.length > 0) {
                var response = {
                  status: 200,
                  data: data.shipping_rates
                }
                return response;
            } else {
              var response = {
                status: 400,
                data: ''
              }
              return response;
            }
        })
        .catch(error => {
          var response = {
            status: 500,
            data: ''
          }
          return response;
        });

        return response;

    } catch(e) {
        alert("Ocorreu um erro ao realizar sua solicitação, por favor tente novamente mais tarde!");
        response = {
          status: 500,
          data: ''
        }
        return response;
    }
  }

  async GetCart() {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: {
          'Content-Type': 'application/json'
      }
    });
    const cart = await response.json();
    return cart;
  }

  async AddCartCoupon(cartId, coupon) {
      const query = `
          mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
              cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
                  userErrors {
                      field
                      message
                  }
              }
          }
      `;

      const variables = {
          cartId: "gid://shopify/Cart/" + cartId,
          discountCodes: coupon
      };

      const response = await fetch('https://dkt-brasil.myshopify.com/api/2024-07/graphql.json', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': 'd8e004b6f1db09142f0be6273ec79acf'
          },
          body: JSON.stringify({ query, variables })
      });

      const responseData = await response.json();

      if (responseData.errors) {
          return false;
      }

      if (responseData.data.cartDiscountCodesUpdate.userErrors.length > 0) {
          return false;
      }

      return true;
  }

  async UpdateResume(cart) {
    const total = cart.total_price;
    const discount = cart.total_discount;
    const shipping_selected = document.getElementById('shipping-select').value;
    var shipping = 0;

    if(shipping_selected != "" && shipping_selected != undefined) {
      shipping = shipping_selected * 100;
    }

    const shippingLine = document.querySelector('.total__shipping');
    if(shipping != 0 && shipping != undefined) {
      const shippingElement = document.querySelector('.totals__shipping-value');
      shippingElement.innerHTML = await CartDrawerActionsInstance.FormatPrice(shipping);
      shippingLine.classList.remove('d-none');
    } else {
      shippingLine.classList.add('d-none');
    }

    const discountLine = document.querySelector('.total__discount');
    if(discount != 0) {
      const discountElement = document.querySelector('.totals__discount-value');
      discountElement.innerHTML = await CartDrawerActionsInstance.FormatPrice(discount);
      discountLine.classList.remove('d-none');
    } else {
      discountLine.classList.add('d-none');
    }

    const totalElement = document.querySelector('.totals__total-value');
    totalElement.innerHTML = await CartDrawerActionsInstance.FormatPrice(cart.total_price + shipping);
  }

  async FormatPrice(value) {
    value = value / 100;
    value = value.toFixed(2).toString();
    if(value == '0') value = '0.00';
    return 'R$ ' + value.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  async ApplyPostalCodeMask(field) {
    const postalCodeInput = document.querySelector(field);
    if (postalCodeInput) {
        const applyMask = (event) => {
            let value = event.target.value;
            value = value.replace(/\D/g, '');
            value = value.replace(/^(\d{5})(\d)/, '$1-$2'); 
            event.target.value = value;
        };

        postalCodeInput.addEventListener('input', applyMask);
        postalCodeInput.addEventListener('keyup', applyMask);
    } else {
        console.warn(`Elemento com o seletor ${field} não foi encontrado.`);
    }
  }

}

const CartDrawerActionsInstance = new CartDrawerActions;
CartDrawerActionsInstance.init();